import type { DayOfWeek, LocalDate, LocalTime } from '../util/datetime';
import { dayOfWeek, minutesOfDay } from '../util/datetime';

/**
 * Modelo de dominio de un restaurante.
 *
 * La opcionalidad de cada campo **es** la especificación de la interfaz: todo campo declarado
 * como `T | null` puede faltar en la respuesta del servidor y, cuando falta, el componente
 * correspondiente **no se renderiza** (no se muestra "N/A", ni un hueco, ni "null").
 * Los campos obligatorios son los que justifican la existencia de la tarjeta: sin `id` ni
 * `name` un restaurante no se puede mostrar y el mapper lo descarta.
 */
export interface Restaurant {
  readonly id: string;
  readonly name: string;
  readonly cuisine: string | null;
  /** 1..4 → "$".."$$$$". Nulo cuando el restaurante no ha declarado rango de precio. */
  readonly priceLevel: number | null;
  /** 0.0..5.0. Nulo cuando aún no hay reseñas verificadas. */
  readonly rating: number | null;
  readonly reviewCount: number;
  readonly photos: readonly string[];
  readonly address: Address | null;
  /** Nulo si el usuario no concedió la ubicación o el servidor no la calculó. */
  readonly distanceMeters: number | null;
  readonly openingHours: readonly OpeningHours[];
  readonly amenities: readonly string[];
  readonly phone: string | null;
  readonly menuUrl: string | null;
  readonly description: string | null;
  readonly promotion: Promotion | null;
  /** `null` significa "no lo sabemos", que es distinto de `false` ("está cerrado"). */
  readonly isCurrentlyOpen: boolean | null;
  /** Slots precargados que vienen con el resultado de búsqueda; vacío ≠ error. */
  readonly previewSlots: readonly TimeSlot[];
}

export interface Address {
  readonly street: string | null;
  readonly neighborhood: string | null;
  readonly city: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
}

export interface OpeningHours {
  readonly dayOfWeek: DayOfWeek;
  readonly opensAt: LocalTime;
  readonly closesAt: LocalTime;
}

export interface Promotion {
  readonly label: string;
  readonly description: string | null;
}

export interface Review {
  readonly id: string;
  readonly authorName: string;
  readonly rating: number;
  readonly comment: string | null;
  readonly date: LocalDate | null;
  readonly foodRating: number | null;
  readonly serviceRating: number | null;
  readonly ambienceRating: number | null;
}

/** Un horario reservable concreto. `token` lo emite el servidor y se revalida al confirmar. */
export interface TimeSlot {
  readonly time: LocalTime;
  readonly token: string;
  readonly available: boolean;
  /** Etiqueta opcional del servidor, p. ej. "Barra" o "Terraza". Nula ⇒ no se muestra. */
  readonly label: string | null;
}

// --------------------------------------------------------------------------- reglas

/** Solo se muestra el bloque de calificación si hay una calificación respaldada por reseñas. */
export function hasRating(restaurant: Restaurant): boolean {
  return restaurant.rating !== null && restaurant.reviewCount > 0;
}

export function primaryPhoto(restaurant: Restaurant): string | null {
  return restaurant.photos[0] ?? null;
}

export function opensOn(restaurant: Restaurant, day: DayOfWeek): boolean {
  return restaurant.openingHours.some((h) => h.dayOfWeek === day);
}

/** Soporta horarios que cruzan la medianoche (p. ej. 18:00–02:00). */
export function hoursContain(hours: OpeningHours, time: LocalTime): boolean {
  const t = minutesOfDay(time);
  const opens = minutesOfDay(hours.opensAt);
  const closes = minutesOfDay(hours.closesAt);
  return closes > opens ? t >= opens && t < closes : t >= opens || t < closes;
}

export function isOpenAt(restaurant: Restaurant, day: DayOfWeek, time: LocalTime): boolean {
  return restaurant.openingHours.filter((h) => h.dayOfWeek === day).some((h) => hoursContain(h, time));
}

export function isOpenOnDate(restaurant: Restaurant, date: LocalDate, time: LocalTime): boolean {
  return isOpenAt(restaurant, dayOfWeek(date), time);
}

/** Línea legible construida solo con las partes presentes; `null` si no hay ninguna. */
export function addressSingleLine(address: Address | null): string | null {
  if (!address) return null;
  const parts = [address.street, address.neighborhood, address.city].filter(
    (part): part is string => !!part && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

export function hasCoordinates(address: Address | null): boolean {
  return !!address && address.latitude !== null && address.longitude !== null;
}
